import React, { useContext, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { BiLoaderAlt } from 'react-icons/bi'
import { FaCheckCircle } from 'react-icons/fa'
import { MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md'
import { CircleHelp, PackagePlus, Search, Store } from 'lucide-react'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './ImBulkAddStock.module.css'

const ImBulkAddStock = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => { try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const vendorIdentifier = JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier
    const authIdentifier = authCtx?.authIdentifier

    const [productsData, setProductsData] = useState([])
    const [filteredData, setFilteredData] = useState([])
    const [selectedItems, setSelectedItems] = useState([]) // array of selected rows
    const [shouldUploadNow, setShouldUploadNow] = useState(false)
    const [isUpdatingStock, setIsUpdatingStock] = useState(false)

    const containerMotion = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                duration: 0.36,
                ease: 'easeOut',
                staggerChildren: 0.08
            }
        }
    }

    const sectionMotion = {
        hidden: { opacity: 0, y: 14 },
        show: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.34, ease: 'easeOut' }
        }
    }

    const tableMotion = {
        hidden: { opacity: 0, y: 16, scale: 0.995 },
        show: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { duration: 0.4, ease: 'easeOut' }
        }
    }
    const getInventoryProducts = async () => {
        const response = await fetch(BASE_URL_API + "getInventoryStock",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    vendorIdentifier: vendorIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier
                })
            });

        if (!response.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const respo = await response.json()
            if (respo?.status === "success") {
                setProductsData(respo?.response)
                setFilteredData(respo?.response)
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                }
                setProductsData([])
                setFilteredData([])
            }
            console.log(respo)
        }
    }

    useEffect(() => {
        getInventoryProducts()
    }, [activeStore?.storeIdentifier])

    const filterData = (data, searchKey) => {
        setFilteredData(data?.filter(item =>
            Object.values(item).some(value =>
                value?.toString().toLowerCase().includes(searchKey.toLowerCase())
            )
        ))
    }

    const toggleSelect = (item) => {
        const exists = selectedItems.find(p => p.productIdentifier === item.productIdentifier)
        if (exists) {
            setSelectedItems(selectedItems.filter(p => p.productIdentifier !== item.productIdentifier))
        } else {
            setSelectedItems([...selectedItems, { ...item, addQuantity: 0 }])
        }
    }

    const handleQuantityChange = (id, value) => {
        setSelectedItems(prev =>
            prev.map(item => {
                if (item.productIdentifier === id) {
                    const addQuantity = Number(value) || 0;
                    const baseQuantity = item?.quantity ?? 0;
                    return {
                        ...item,
                        quantity: parseFloat(baseQuantity),
                        addQuantity,
                        finalQuantity: parseFloat(baseQuantity) + parseFloat(addQuantity)
                    };
                }
                return item;
            })
        );
    };

    const inputRefs = useRef([])

    const handleKeyDown = (e, index) => {
        if (e.key === "Enter" || e.key === "ArrowDown") {
            e.preventDefault()
            const next = inputRefs.current[index + 1]
            if (next) next.focus()
        } else if (e.key === "ArrowUp") {
            e.preventDefault()
            const prev = inputRefs.current[index - 1]
            if (prev) prev.focus()
        }
    }

    const isSelected = (id) => selectedItems.some(item => item.productIdentifier === id)

    const updateStock = async () => {
        // console.log(selectedItems)
        setIsUpdatingStock(true)
        const response = await fetch(BASE_URL_API + "updateInventoryStock",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    allData: selectedItems,
                    vendorIdentifier: vendorIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier,
                    authIdentifier
                })
            });

        if (!response.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const respo = await response.json()
            if (respo?.status === "success") {
                toast(`Stock has been successfully updated!`)
                getInventoryProducts()
                setSelectedItems([])
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                }
            }
        }
        setIsUpdatingStock(false)
    }

    const allSelected = filteredData.length > 0 && filteredData.every(item => isSelected(item.productIdentifier));
    const someSelected = filteredData.some(item => isSelected(item.productIdentifier));

    const toggleSelectAll = () => {
        if (allSelected) {
            const filteredIds = new Set(filteredData.map(item => item.productIdentifier));
            setSelectedItems(prev => prev.filter(item => !filteredIds.has(item.productIdentifier)));
        } else {
            const mappedItems = filteredData.map(item => {
                const existing = selectedItems.find(sel => sel.productIdentifier === item.productIdentifier)
                return existing || { ...item, addQuantity: 0 }
            })

            const filteredIds = new Set(filteredData.map(item => item.productIdentifier));
            const preserved = selectedItems.filter(item => !filteredIds.has(item.productIdentifier))
            setSelectedItems([...preserved, ...mappedItems]);
        }
    };

    const isReadyToUpload = () => {
        if (!Array.isArray(selectedItems) || selectedItems.length === 0) return false;

        return selectedItems.every(item => {
            const v = item?.addQuantity;
            // must be present, numeric, and > 0
            if (v === null || v === undefined || v === '') return false;
            const n = Number(v);
            return Number.isFinite(n) && n > 0;
        });
    };

    useEffect(() => {
        setShouldUploadNow(isReadyToUpload())
    }, [selectedItems])




    if (activeStore === null) {
        return (
            <motion.div className={styles.noStoreWrapper} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
                <motion.div
                    className={styles.noStoreCard}
                    initial={{ opacity: 0, y: 16, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.36, ease: 'easeOut' }}
                >
                    <Store size={26} className={styles.noStoreIcon} />
                    <h3>No Store Selected</h3>
                    <p>Please select a store first to add stock in bulk.</p>
                </motion.div>
            </motion.div>
        )
    }

    return (
        <motion.div
            className={styles.mainWrapper}
            variants={containerMotion}
            initial="hidden"
            animate="show"
        >
            <motion.div className={styles.pageHeader} variants={sectionMotion}>
                <div className={styles.pageHeaderLeft}>
                    <span className={styles.statusPill}>Inventory Management</span>
                    <h1 className={styles.pageTitle}>Bulk Stock Updater</h1>
                    <p className={styles.pageSubtitle}>Select products, add quantities, and sync stock in one action.</p>
                </div>
                <div className={styles.helpChip}>
                    <CircleHelp size={15} /> Need Help?
                </div>
            </motion.div>

            <motion.div className={styles.messageCard} variants={sectionMotion}>
                <div className={styles.messageIcon}><FaCheckCircle size={16} /></div>
                <div className={styles.messageText}>
                    Products have been imported successfully. Update stock levels in bulk to keep inventory accurate.
                </div>
            </motion.div>

            <motion.div className={styles.controlBar} variants={sectionMotion}>
                <div className={styles.searchBox}>
                    <Search size={16} className={styles.searchIcon} />
                    <input className={styles.searchInput} placeholder="Search products, category, SKU..." onChange={(e) => filterData(productsData, e.target.value)} />
                </div>
                <div className={styles.metaBadges}>
                    <div className={styles.metaBadge}><PackagePlus size={14} /> Total: {productsData?.length || 0}</div>
                    <div className={styles.metaBadge}><MdCheckBox size={15} /> Selected: {selectedItems?.length || 0}</div>
                    <div className={styles.metaBadge}><Store size={14} /> Visible: {filteredData?.length || 0}</div>
                </div>
            </motion.div>

            {productsData?.length > 0 ?
                <motion.div className={styles.tableCard} variants={tableMotion}>
                    <div className={styles.tableHeader}>
                        <button className={styles.checkboxBtn} onClick={toggleSelectAll}>
                            {allSelected ? (
                                <MdCheckBox size={18} color="#4f46e5" />
                            ) : someSelected ? (
                                <MdCheckBox size={18} color="#2563eb" />
                            ) : (
                                <MdCheckBoxOutlineBlank size={18} color="#64748b" />
                            )}
                        </button>
                        <div className={styles.productName}>Product Name</div>
                        <div className={styles.categoryName}>Category</div>
                        <div className={styles.barcode}>SKU / Barcode</div>
                        <div className={styles.mrp}>Current Stock</div>
                        <div className={styles.price}>Add Quantity</div>
                        <div className={styles.stock}>Final Stock</div>
                    </div>
                    <div className={styles.tableBody}>
                        <div className={styles.productWrapper}>
                            {filteredData?.map((item, index) => {
                                const selected = isSelected(item.productIdentifier)
                                const selectedData = selectedItems.find(p => p.productIdentifier === item.productIdentifier)

                                return (
                                    <div key={index} className={`${styles.eachRowWrapper} ${selected ? styles.rowSelected : ''}`}>
                                        <button className={styles.checkboxBtn} onClick={() => toggleSelect(item)}>
                                            {selected ? <MdCheckBox size={18} color="#4f46e5" /> : <MdCheckBoxOutlineBlank size={18} color="#94a3b8" />}
                                        </button>
                                        <div className={styles.productName} onClick={() => toggleSelect(item)}>
                                            {item?.productName}
                                        </div>
                                        <div className={styles.categoryName}>
                                            {item?.categoryName}
                                        </div>
                                        <div className={styles.barcode}>
                                            {item?.skuCode || item?.barCode}
                                        </div>
                                        <div className={styles.mrp}>
                                            {item?.quantity}
                                        </div>
                                        <div className={styles.price}>
                                            {selected ? (
                                                <input
                                                    ref={el => (inputRefs.current[index] = el)}
                                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                                    type="number"
                                                    min="0"
                                                    value={selectedData?.addQuantity || ""}
                                                    onChange={(e) => handleQuantityChange(item.productIdentifier, e.target.value)}
                                                    placeholder="0"
                                                />
                                            ) : (
                                                <span className={styles.disabledInput}>--</span>
                                            )}
                                        </div>
                                        <div className={styles.stock}>
                                            {selected ? (parseFloat(item?.quantity || 0) + parseFloat(selectedData?.addQuantity || 0)) : parseFloat(item?.quantity || 0)}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </motion.div>
                :
                <motion.div className={styles.emptyState} variants={tableMotion}>
                    <PackagePlus size={22} />
                    <h3>No products available</h3>
                    <p>You have not added any product in inventory yet.</p>
                </motion.div>
            }

            <motion.div className={styles.actionWrapper} variants={sectionMotion}>
                <div className={styles.actionContainer}>
                    <div className={styles.selectionMeta}>
                        {selectedItems.length > 0 ? (
                            <span>{selectedItems.length} item(s) selected for stock update</span>
                        ) : (
                            <span>Select products to start adding quantities</span>
                        )}
                    </div>
                    <div className={styles.actionRight}>
                        {shouldUploadNow === true ?
                            <>
                                {isUpdatingStock === true ?
                                    <button className={styles.updateBtnLoader} disabled={isUpdatingStock} >
                                        <BiLoaderAlt size={15} className={styles.spinner} /> Updating Stock...
                                    </button>
                                    :
                                    <button className={styles.updateBtn} onClick={() => updateStock()} >Confirm & Update</button>
                                }
                            </>
                            :
                            <button className={styles.updateBtn} onClick={() => toast('Kindly provide quantity to all selected items.')} >Confirm & Update</button>
                        }
                    </div>
                </div>
            </motion.div>
        </motion.div>
    )
}

export default ImBulkAddStock
